/**
 * Scraper Module - Admin UI
 * 
 * Features:
 * - Create scrape jobs
 * - View job progress and results
 * - Action queue for leads needing human review
 * - Export to CSV
 * 
 * All scraping runs in background workers - UI only shows status.
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Plus,
  RefreshCw,
  Download,
  Eye,
  X,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Users,
  Mail,
  Globe,
  Facebook,
  FileText,
  ChevronLeft,
  ChevronRight,
  Filter,
  MoreHorizontal
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL || "";

// US States
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

// Job status badge colors
const STATUS_COLORS = {
  queued: "bg-yellow-100 text-yellow-800",
  running: "bg-blue-100 text-blue-800",
  complete: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800"
};

// Trust score badge
const TrustScoreBadge = ({ score, reasons }) => {
  if (score === null || score === undefined) return <span className="text-muted-foreground">—</span>;
  
  let colorClass = "bg-red-100 text-red-800";
  if (score >= 70) colorClass = "bg-green-100 text-green-800";
  else if (score >= 40) colorClass = "bg-yellow-100 text-yellow-800";
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge className={colorClass}>{score}</Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="text-xs space-y-1">
            {reasons?.map((r, i) => <div key={i}>{r}</div>) || "No details"}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default function ScraperPage() {
  const [activeTab, setActiveTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [results, setResults] = useState([]);
  const [actionQueue, setActionQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scraperStatus, setScraperStatus] = useState(null);
  
  // Pagination
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [actionPage, setActionPage] = useState(1);
  const [actionTotal, setActionTotal] = useState(0);
  const pageSize = 50;
  
  // Modals
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  
  // Create job form
  const [jobForm, setJobForm] = useState({
    keyword: "",
    city: "",
    state: "TX",
    result_limit: 200,
    sources: {
      yellowpages: true,
      bbb: false,
      yelp: false
    },
    website_enrichment_enabled: true
  });
  
  // Filters
  const [filters, setFilters] = useState({
    email_status: "",
    min_trust_score: "",
    needs_human: "",
    has_facebook: "",
    has_website: ""
  });

  // Fetch scraper status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/scraper/status`, { withCredentials: true });
      setScraperStatus(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setScraperStatus({ enabled: false });
      }
    }
  }, []);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/scraper/jobs`, {
        params: { page: jobsPage, page_size: pageSize },
        withCredentials: true
      });
      setJobs(res.data.items);
      setJobsTotal(res.data.total);
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    } finally {
      setLoading(false);
    }
  }, [jobsPage]);

  // Fetch results for a job
  const fetchResults = useCallback(async (jobId) => {
    if (!jobId) return;
    try {
      setLoading(true);
      const params = { page: resultsPage, page_size: pageSize };
      if (filters.email_status) params.email_status = filters.email_status;
      if (filters.min_trust_score) params.min_trust_score = parseInt(filters.min_trust_score);
      if (filters.needs_human) params.needs_human = filters.needs_human === "true";
      if (filters.has_facebook) params.has_facebook = filters.has_facebook === "true";
      if (filters.has_website) params.has_website = filters.has_website === "true";
      
      const res = await axios.get(`${API}/api/admin/scraper/jobs/${jobId}/results`, {
        params,
        withCredentials: true
      });
      setResults(res.data.items);
      setResultsTotal(res.data.total);
    } catch (error) {
      console.error("Failed to fetch results:", error);
    } finally {
      setLoading(false);
    }
  }, [resultsPage, filters]);

  // Fetch action queue
  const fetchActionQueue = useCallback(async (jobId) => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API}/api/admin/scraper/jobs/${jobId}/action-queue`, {
        params: { page: actionPage, page_size: pageSize },
        withCredentials: true
      });
      setActionQueue(res.data.items);
      setActionTotal(res.data.total);
    } catch (error) {
      console.error("Failed to fetch action queue:", error);
    }
  }, [actionPage]);

  // Fetch job detail (includes summary stats)
  const fetchJobDetail = useCallback(async (jobId) => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API}/api/admin/scraper/jobs/${jobId}`, {
        withCredentials: true
      });
      setSelectedJob(res.data);
    } catch (error) {
      console.error("Failed to fetch job detail:", error);
    }
  }, []);

  // Create job
  const handleCreateJob = async () => {
    if (!jobForm.keyword || !jobForm.city || !jobForm.state) {
      toast.error("Please fill in keyword, city, and state");
      return;
    }
    
    try {
      setLoading(true);
      const res = await axios.post(`${API}/api/admin/scraper/jobs`, jobForm, { withCredentials: true });
      toast.success(`Job created: ${res.data.job_id}`);
      setShowCreateJob(false);
      setJobForm({
        keyword: "",
        city: "",
        state: "TX",
        result_limit: 200,
        sources: { yellowpages: true, bbb: false, yelp: false },
        website_enrichment_enabled: true
      });
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create job");
    } finally {
      setLoading(false);
    }
  };

  // Cancel job
  const handleCancelJob = async (jobId) => {
    try {
      await axios.post(`${API}/api/admin/scraper/jobs/${jobId}/cancel`, {}, { withCredentials: true });
      toast.success("Job cancelled");
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel job");
    }
  };

  // Suppress lead
  const handleSuppressLead = async (leadId, type) => {
    try {
      await axios.post(`${API}/api/admin/scraper/leads/${leadId}/suppress`, {
        suppression_type: type
      }, { withCredentials: true });
      toast.success(`Lead marked as ${type}`);
      if (selectedJob) {
        fetchResults(selectedJob.id);
        fetchActionQueue(selectedJob.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to suppress lead");
    }
  };

  // Mark reviewed
  const handleMarkReviewed = async (leadId) => {
    try {
      await axios.post(`${API}/api/admin/scraper/leads/${leadId}/mark-reviewed`, {}, { withCredentials: true });
      toast.success("Lead marked as reviewed");
      if (selectedJob) {
        fetchActionQueue(selectedJob.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark lead");
    }
  };

  // Export
  const handleExport = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/scraper/exports`, {
        job_id: selectedJob?.id,
        filters: filters.email_status || filters.min_trust_score ? filters : null
      }, { withCredentials: true });
      toast.success(`Export queued: ${res.data.export_id}`);
      setShowExportDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create export");
    }
  };

  // Initial load
  useEffect(() => {
    fetchStatus();
    fetchJobs();
  }, [fetchStatus, fetchJobs]);

  // Refresh jobs on page change
  useEffect(() => {
    fetchJobs();
  }, [jobsPage, fetchJobs]);

  // Fetch results when job selected or filters change
  useEffect(() => {
    if (selectedJob) {
      fetchResults(selectedJob.id);
      fetchActionQueue(selectedJob.id);
      // Fetch job detail to get summary stats if not already present
      if (!selectedJob.summary) {
        fetchJobDetail(selectedJob.id);
      }
    }
  }, [selectedJob, resultsPage, filters, fetchResults, fetchActionQueue, fetchJobDetail]);

  // Auto-refresh running jobs
  useEffect(() => {
    const hasRunning = jobs.some(j => j.status === "running" || j.status === "queued");
    if (hasRunning) {
      const interval = setInterval(fetchJobs, 5000);
      return () => clearInterval(interval);
    }
  }, [jobs, fetchJobs]);

  // If scraper is disabled
  if (scraperStatus && !scraperStatus.enabled) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle size={48} className="mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Scraper Module Disabled</h2>
            <p className="text-muted-foreground">
              Set <code className="bg-muted px-2 py-1 rounded">SCRAPER_ENABLED=true</code> in your environment to enable this feature.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="scraper-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Scraper</h1>
          <p className="text-muted-foreground">Discover and enrich leads from public directories</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchJobs} disabled={loading}>
            <RefreshCw size={16} className={loading ? "animate-spin mr-2" : "mr-2"} />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateJob(true)}>
            <Plus size={16} className="mr-2" />
            New Scrape Job
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      {scraperStatus?.worker_running && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-green-600" />
          <span className="text-green-800 text-sm">
            Worker active | Jobs queued: {scraperStatus.jobs?.queued || 0} | Running: {scraperStatus.jobs?.running || 0}
          </span>
        </div>
      )}

      {/* Main Content */}
      {!selectedJob ? (
        // Jobs List View
        <Card>
          <CardHeader>
            <CardTitle>Scrape Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            {jobs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Search size={48} className="mx-auto mb-4 opacity-50" />
                <p>No scrape jobs yet</p>
                <Button className="mt-4" onClick={() => setShowCreateJob(true)}>
                  Create Your First Job
                </Button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Query</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Discovered</th>
                        <th className="text-left p-3 font-medium">Emails</th>
                        <th className="text-left p-3 font-medium">Action Needed</th>
                        <th className="text-left p-3 font-medium">Created</th>
                        <th className="text-right p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map(job => (
                        <tr key={job.id} className="border-b hover:bg-muted/50">
                          <td className="p-3">
                            <div className="font-medium">{job.keyword}</div>
                            <div className="text-sm text-muted-foreground">{job.city}, {job.state}</div>
                          </td>
                          <td className="p-3">
                            <Badge className={STATUS_COLORS[job.status] || ""}>{job.status}</Badge>
                          </td>
                          <td className="p-3">{job.progress_json?.discovered || 0}</td>
                          <td className="p-3">{job.progress_json?.emails_found || 0}</td>
                          <td className="p-3">
                            {(job.progress_json?.needs_human || 0) > 0 && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700">
                                {job.progress_json.needs_human}
                              </Badge>
                            )}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {new Date(job.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" onClick={() => setSelectedJob(job)}>
                                <Eye size={14} />
                              </Button>
                              {(job.status === "queued" || job.status === "running") && (
                                <Button size="sm" variant="ghost" onClick={() => handleCancelJob(job.id)}>
                                  <X size={14} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {jobsTotal > pageSize && (
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      Showing {((jobsPage - 1) * pageSize) + 1} - {Math.min(jobsPage * pageSize, jobsTotal)} of {jobsTotal}
                    </span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={jobsPage === 1} onClick={() => setJobsPage(p => p - 1)}>
                        <ChevronLeft size={14} />
                      </Button>
                      <Button size="sm" variant="outline" disabled={jobsPage * pageSize >= jobsTotal} onClick={() => setJobsPage(p => p + 1)}>
                        <ChevronRight size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        // Job Detail View
        <>
          <Button variant="ghost" onClick={() => setSelectedJob(null)}>
            <ChevronLeft size={16} className="mr-2" />
            Back to Jobs
          </Button>
          
          {/* Job Summary */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{selectedJob.keyword} - {selectedJob.city}, {selectedJob.state}</CardTitle>
                  <Badge className={`mt-2 ${STATUS_COLORS[selectedJob.status]}`}>{selectedJob.status}</Badge>
                </div>
                <Button onClick={() => setShowExportDialog(true)}>
                  <Download size={16} className="mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold">{selectedJob.summary?.total_leads || 0}</div>
                  <div className="text-sm text-muted-foreground">Total Leads</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{selectedJob.summary?.leads_with_email || 0}</div>
                  <div className="text-sm text-muted-foreground">With Email</div>
                  {selectedJob.summary?.email_yield_rate > 0 && (
                    <div className="text-xs text-green-600 mt-1">{selectedJob.summary.email_yield_rate}% yield</div>
                  )}
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-amber-600">{selectedJob.summary?.leads_need_human || 0}</div>
                  <div className="text-sm text-muted-foreground">Need Review</div>
                  {selectedJob.summary?.needs_human_rate > 0 && (
                    <div className="text-xs text-amber-600 mt-1">{selectedJob.summary.needs_human_rate}%</div>
                  )}
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{selectedJob.summary?.avg_trust_score || '-'}</div>
                  <div className="text-sm text-muted-foreground">Avg Trust Score</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold">{selectedJob.summary?.leads_with_contact_form || 0}</div>
                  <div className="text-sm text-muted-foreground">Has Contact Form</div>
                </div>
              </div>
              
              {/* Source Breakdown (if multiple sources) */}
              {selectedJob.summary?.source_breakdown && Object.keys(selectedJob.summary.source_breakdown).length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="text-muted-foreground">Sources:</span>
                  {Object.entries(selectedJob.summary.source_breakdown).map(([source, count]) => (
                    <span key={source} className="px-2 py-0.5 bg-muted rounded">
                      {source === 'unknown' ? 'legacy' : source}: {count}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabs: Results / Action Queue */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="results">
                <Users size={14} className="mr-2" />
                Results ({resultsTotal})
              </TabsTrigger>
              <TabsTrigger value="action">
                <AlertCircle size={14} className="mr-2" />
                Action Queue ({actionTotal})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="results" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Results</CardTitle>
                    {/* Filters */}
                    <div className="flex gap-2">
                      <Select value={filters.email_status || "all"} onValueChange={v => setFilters(f => ({ ...f, email_status: v === "all" ? "" : v }))}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Email Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="found">Found</SelectItem>
                          <SelectItem value="inferred">Inferred</SelectItem>
                          <SelectItem value="none">None</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={filters.min_trust_score || "any"} onValueChange={v => setFilters(f => ({ ...f, min_trust_score: v === "any" ? "" : v }))}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Trust Score" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="70">≥70 (High)</SelectItem>
                          <SelectItem value="40">≥40 (Medium)</SelectItem>
                          <SelectItem value="1">≥1</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Business</th>
                          <th className="text-left p-2">Location</th>
                          <th className="text-left p-2">Phone</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Trust</th>
                          <th className="text-left p-2">Links</th>
                          <th className="text-right p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map(lead => (
                          <tr key={lead.id} className="border-b hover:bg-muted/50">
                            <td className="p-2">
                              <div className="font-medium">{lead.business_name}</div>
                            </td>
                            <td className="p-2 text-muted-foreground">{lead.city}, {lead.state}</td>
                            <td className="p-2">{lead.phone_raw || "—"}</td>
                            <td className="p-2">
                              {lead.primary_email ? (
                                <span className="text-green-600">{lead.primary_email}</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="p-2">
                              <TrustScoreBadge score={lead.trust_score} reasons={lead.trust_reasons_json} />
                            </td>
                            <td className="p-2">
                              <div className="flex gap-1">
                                {lead.website_url && (
                                  <a href={lead.website_url} target="_blank" rel="noopener noreferrer">
                                    <Globe size={14} className="text-blue-500" />
                                  </a>
                                )}
                                {lead.facebook_url && (
                                  <a href={lead.facebook_url} target="_blank" rel="noopener noreferrer">
                                    <Facebook size={14} className="text-blue-600" />
                                  </a>
                                )}
                                {lead.contact_form_url && (
                                  <a href={lead.contact_form_url} target="_blank" rel="noopener noreferrer">
                                    <FileText size={14} className="text-green-500" />
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="p-2 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost">
                                    <MoreHorizontal size={14} />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleSuppressLead(lead.id, "no_email")}>
                                    Mark No Email
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleSuppressLead(lead.id, "do_not_contact")}>
                                    Do Not Contact
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {resultsTotal > pageSize && (
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-sm text-muted-foreground">
                        Showing {((resultsPage - 1) * pageSize) + 1} - {Math.min(resultsPage * pageSize, resultsTotal)} of {resultsTotal}
                      </span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={resultsPage === 1} onClick={() => setResultsPage(p => p - 1)}>
                          <ChevronLeft size={14} />
                        </Button>
                        <Button size="sm" variant="outline" disabled={resultsPage * pageSize >= resultsTotal} onClick={() => setResultsPage(p => p + 1)}>
                          <ChevronRight size={14} />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="action" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle size={18} className="text-amber-500" />
                    Action Queue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {actionQueue.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle2 size={48} className="mx-auto mb-4 text-green-500" />
                      <p>No leads need human action!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {actionQueue.map(lead => (
                        <div key={lead.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{lead.business_name}</div>
                              <div className="text-sm text-muted-foreground">{lead.city}, {lead.state}</div>
                              <div className="mt-2">
                                <Badge variant="outline" className="bg-amber-50 text-amber-700">
                                  {lead.next_action?.replace(/_/g, " ")}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {lead.facebook_url && (
                                <Button size="sm" variant="outline" asChild>
                                  <a href={lead.facebook_url} target="_blank" rel="noopener noreferrer">
                                    <Facebook size={14} className="mr-1" />
                                    Facebook
                                  </a>
                                </Button>
                              )}
                              {lead.contact_form_url && (
                                <Button size="sm" variant="outline" asChild>
                                  <a href={lead.contact_form_url} target="_blank" rel="noopener noreferrer">
                                    <FileText size={14} className="mr-1" />
                                    Contact Form
                                  </a>
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => handleSuppressLead(lead.id, "no_email")}>
                                No Email
                              </Button>
                              <Button size="sm" onClick={() => handleMarkReviewed(lead.id)}>
                                Mark Reviewed
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Create Job Dialog */}
      <Dialog open={showCreateJob} onOpenChange={setShowCreateJob}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Scrape Job</DialogTitle>
            <DialogDescription>
              Search for businesses in public directories
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Service Keyword *</Label>
              <Input
                placeholder="e.g., HVAC, Plumber, Electrician"
                value={jobForm.keyword}
                onChange={e => setJobForm(f => ({ ...f, keyword: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City *</Label>
                <Input
                  placeholder="e.g., Dallas"
                  value={jobForm.city}
                  onChange={e => setJobForm(f => ({ ...f, city: e.target.value }))}
                />
              </div>
              <div>
                <Label>State *</Label>
                <Select value={jobForm.state} onValueChange={v => setJobForm(f => ({ ...f, state: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(st => (
                      <SelectItem key={st} value={st}>{st}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Result Limit</Label>
              <Input
                type="number"
                min={1}
                max={2000}
                value={jobForm.result_limit}
                onChange={e => setJobForm(f => ({ ...f, result_limit: parseInt(e.target.value) || 200 }))}
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Sources</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">YellowPages</span>
                  <Switch
                    checked={jobForm.sources.yellowpages}
                    onCheckedChange={v => setJobForm(f => ({ ...f, sources: { ...f.sources, yellowpages: v } }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">BBB (Coming Soon)</span>
                  <Switch disabled checked={false} />
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm">Website Enrichment</span>
              <Switch
                checked={jobForm.website_enrichment_enabled}
                onCheckedChange={v => setJobForm(f => ({ ...f, website_enrichment_enabled: v }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateJob(false)}>Cancel</Button>
            <Button onClick={handleCreateJob} disabled={loading}>
              {loading && <Loader2 size={14} className="mr-2 animate-spin" />}
              Run Scrape
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Results</DialogTitle>
            <DialogDescription>
              Export leads to CSV. Export runs in background.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will export {resultsTotal} leads from the current job with applied filters.
              Suppressed leads are excluded by default.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Cancel</Button>
            <Button onClick={handleExport}>
              <Download size={14} className="mr-2" />
              Start Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
